<template>
  <div class="menu">
    <v-menu
      v-model="visibility"
      :class="{ 'v-menu__activator--disabled': isLoading }"
      :left="icon"
      :close-on-content-click="false"
      :open-on-click="false"
      offset-y
    >
      <v-btn
        slot="activator"
        :disabled="isLoading"
        class="mx-0 px-2 pl-3"
        flat
        @click="toggleDropdown"
      >
        <v-icon v-if="icon" color="gray-6" class="mr-1">today</v-icon>

        <span
          class="text--normal"
          :class="[
            { 'autocomplete__text': !icon },
            { 'autocomplete__text--active': !icon && selectedPeriod }
          ]"
        >
          {{ $t(`${namespace}.${selectedPeriod}`) }}
        </span>

        <v-icon
          v-if="selectedPeriod !== 'currentMonth'"
          class="autocomplete__arrow"
          data-id="menu-clear"
          color="accent"
          right
          small
          @click="resetPeriod"
        >
          close
        </v-icon>

        <v-icon
          v-else
          class="autocomplete__arrow"
          color="accent"
          right
          small
        >
          keyboard_arrow_down
        </v-icon>
      </v-btn>

      <v-list v-if="!isLoading">
        <v-list-tile @click="getPeriod('currentMonth')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.currentMonth`) }}</span>
          </v-list-tile-title>
        </v-list-tile>

        <v-list-tile @click="getPeriod('lastWeek')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.lastWeek`) }}</span>
          </v-list-tile-title>
        </v-list-tile>

        <v-list-tile @click="getPeriod('lastMonth')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.lastMonth`) }}</span>
          </v-list-tile-title>
        </v-list-tile>

        <v-list-tile @click="getPeriod('lastThreeMonths')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.lastThreeMonths`) }}</span>
          </v-list-tile-title>
        </v-list-tile>

        <v-list-tile @click="getPeriod('today')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.today`) }}</span>
          </v-list-tile-title>
        </v-list-tile>

        <v-divider />

        <v-list-tile @click="getPeriod('custom')">
          <v-list-tile-title>
            <span class="menu__item__text">{{ $t(`${namespace}.customPeriod`) }}</span>
          </v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>

    <PeriodDialog
      :visible="dialog"
      :time="time"
      @applyPeriod="applyCustomPeriod"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import PeriodDialog from './PeriodDialog'

export default {
  name: 'MenuItemPeriod',
  props: {
    icon: {
      type: Boolean,
      default: false
    },
    status: {
      type: Number,
      default: STATUS_SUCCESS
    },
    time: {
      type: Boolean,
      default: true
    }
  },
  components: {
    PeriodDialog
  },
  computed: {
    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Components.MenuItemPeriod',
      visibility: false,
      selectedPeriod: 'currentMonth',
      dialog: false,
      YYYY_MM_DD: 'YYYY-MM-DD',
      YYYY_MM_: 'YYYY-MM-',
      startDate: null,
      endDate: null
    }
  },
  methods: {
    setPeriod (period) {
      this.selectedPeriod = period
      this.$emit('changePeriod', period)
    },

    openDialog () {
      this.dialog = true
    },

    closeDialog (period) {
      this.setPeriod(period)
      this.dialog = false
    },

    emitPeriod () {
      this.$emit('change', {
        startDate: this.startDate,
        endDate: this.endDate
      })
    },

    currentMonth () {
      const today = new Date()
      const day = String(today.getDate()).padStart(2, '0')
      const daysInMonth = moment().daysInMonth()
      const days = Number(day) < daysInMonth ? day : daysInMonth
      this.startDate = moment().format(this.YYYY_MM_) + '01'
      this.endDate = moment().format(this.YYYY_MM_) + days
      this.emitPeriod()
    },

    lastWeek () {
      this.startDate = moment().subtract(1, 'weeks').startOf('isoWeek').subtract(1, 'days').format(this.YYYY_MM_DD)
      this.endDate = moment().subtract(1, 'weeks').endOf('isoWeek').subtract(1, 'days').format(this.YYYY_MM_DD)
      this.emitPeriod()
    },

    lastMonth () {
      const subtractOneMonth = moment().subtract(1, 'months')
      this.startDate = subtractOneMonth.format(this.YYYY_MM_) + '01'
      this.endDate = subtractOneMonth.format(this.YYYY_MM_) + subtractOneMonth.daysInMonth()
      this.emitPeriod()
    },

    lastThreeMonths () {
      const subtractThreeMonth = moment().subtract(3, 'months')
      const subtractOneMonth = moment().subtract(1, 'months')
      this.startDate = subtractThreeMonth.format(this.YYYY_MM_) + '01'
      this.endDate = subtractOneMonth.format(this.YYYY_MM_) + subtractOneMonth.daysInMonth()
      this.emitPeriod()
    },

    today () {
      const endTime = moment().format('HH:mm')
      this.startDate = `${moment().format(this.YYYY_MM_DD)}T00:00`
      this.endDate = `${moment().format(this.YYYY_MM_DD)}T${endTime}`
      this.emitPeriod()
    },

    custom () {
      this.openDialog()
    },

    applyCustomPeriod ({ startDate, endDate }) {
      this.startDate = startDate
      this.endDate = endDate
      this.emitPeriod()
    },

    getPeriod (period) {
      this.visibility = false

      if (period === 'custom' || this.selectedPeriod !== period) {
        switch (period) {
          case 'currentMonth':
            this.setPeriod('currentMonth')
            this.currentMonth()
            break

          case 'lastWeek':
            this.setPeriod('lastWeek')
            this.lastWeek()
            break

          case 'lastMonth':
            this.setPeriod('lastMonth')
            this.lastMonth()
            break

          case 'lastThreeMonths':
            this.setPeriod('lastThreeMonths')
            this.lastThreeMonths()
            break

          case 'today':
            this.setPeriod('today')
            this.today()
            break

          case 'custom':
            this.custom()
            break
        }
      }
    },

    resetPeriod () {
      this.startDate = null
      this.endDate = null
      this.setPeriod('currentMonth')
      this.emitPeriod()
    },

    toggleDropdown ($event) {
      if ($event.target.dataset.id !== 'menu-clear') {
        this.visibility = true
      }
    }
  },
  mounted () {
    this.currentMonth()
  }
}
</script>

<style lang="scss">
.menu__item__text {
  font-size: 14px;
}
</style>
